import React, { createContext, useContext, useMemo } from 'react';
import { useEntitlements } from './EntitlementsContext';

const CreditsContext = createContext(null);

const CREDIT_META = {
  emailUnlocksCount: {
    label: 'Email Credits',
    description: 'Used to unlock verified work emails in Discovery.'
  },
  exportsCount: {
    label: 'Export Credits',
    description: 'Consumed by Discovery CSV exports.'
  },
  adoptionsCount: {
    label: 'Manual Adoption Credits',
    description: 'Used only when adding contacts without email unlock.'
  },
  searchQueriesCount: {
    label: 'Search Credits',
    description: 'Consumed by premium Discovery searches.'
  },
  bulkOperationsCount: {
    label: 'Bulk Credits',
    description: 'Used for high-volume bulk actions in Discovery.'
  }
};

const CREDIT_ORDER = [
  'emailUnlocksCount',
  'exportsCount',
  'adoptionsCount',
  'searchQueriesCount',
  'bulkOperationsCount'
];

const LEGACY_BUCKET_MAP = {
  emailUnlocksCount: { domain: 'credits', key: 'emailCredits' },
  exportsCount: { domain: 'credits', key: 'discoveryExportCredits' },
  adoptionsCount: { domain: 'credits', key: 'manualAdoptionCredits' },
  searchQueriesCount: { domain: 'quotas', key: 'monthlySearchQueries' },
  bulkOperationsCount: { domain: 'quotas', key: 'monthlyBulkOperations' }
};

const normalizeBucket = (legacyKey, snapshot = null) => {
  const map = LEGACY_BUCKET_MAP[legacyKey];
  const sourceBucket = snapshot?.[map?.domain]?.[map?.key] || null;
  const meta = CREDIT_META[legacyKey] || { label: legacyKey, description: 'Workspace credit bucket.' };

  const inferredUnlimited = sourceBucket?.isUnlimited
    || (sourceBucket?.limit === null && sourceBucket?.remaining === null && sourceBucket?.isReached === false);

  const total = inferredUnlimited
    ? Infinity
    : (Number.isFinite(Number(sourceBucket?.total)) ? Number(sourceBucket.total) : 0);
  const used = Number.isFinite(Number(sourceBucket?.used)) ? Number(sourceBucket.used) : 0;
  const topUp = Number.isFinite(Number(sourceBucket?.topUp)) ? Number(sourceBucket.topUp) : 0;
  const base = total === Infinity ? Infinity : Math.max(0, total - topUp);
  const remaining = inferredUnlimited || sourceBucket?.remaining === Infinity
    ? Infinity
    : (Number.isFinite(Number(sourceBucket?.remaining)) ? Number(sourceBucket.remaining) : Math.max(0, total - used));

  const pctUsed = total > 0 && total !== Infinity ? Math.min(100, Math.round((used / total) * 100)) : 0;

  return {
    key: legacyKey,
    label: meta.label,
    description: meta.description,
    base,
    topUp,
    used,
    total,
    remaining,
    pctUsed
  };
};

export const useWorkspaceCredits = () => {
  const context = useContext(CreditsContext);
  if (!context) throw new Error('useWorkspaceCredits must be used within a CreditsProvider');
  return context;
};

export const CreditsProvider = ({ children }) => {
  const {
    snapshot,
    planCode,
    planStatus,
    isLoading,
    error,
    lastSyncedAt,
    refreshEntitlements
  } = useEntitlements();

  const creditBuckets = useMemo(() => (
    CREDIT_ORDER.map((legacyKey) => normalizeBucket(legacyKey, snapshot))
  ), [snapshot]);

  const primaryEmailCredits = useMemo(
    () => creditBuckets.find((bucket) => bucket.key === 'emailUnlocksCount')?.remaining ?? 0,
    [creditBuckets]
  );

  const otherCreditBuckets = useMemo(
    () => creditBuckets.filter((bucket) => bucket.key !== 'emailUnlocksCount'),
    [creditBuckets]
  );

  const commercialState = useMemo(() => {
    const baseQuota = {};
    const topUps = {};
    const usage = {};

    creditBuckets.forEach((bucket) => {
      baseQuota[bucket.key] = bucket.base;
      topUps[bucket.key] = bucket.topUp;
      usage[bucket.key] = bucket.used;
    });

    return {
      planCode,
      status: planStatus,
      baseQuota,
      topUps,
      usage
    };
  }, [creditBuckets, planCode, planStatus]);

  const value = useMemo(() => ({
    commercialState,
    creditBuckets,
    otherCreditBuckets,
    primaryEmailCredits,
    isLoading,
    error,
    lastSyncedAt,
    planCode,
    status: planStatus,
    refreshCredits: refreshEntitlements
  }), [
    commercialState,
    creditBuckets,
    otherCreditBuckets,
    primaryEmailCredits,
    isLoading,
    error,
    lastSyncedAt,
    planCode,
    planStatus,
    refreshEntitlements
  ]);

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
};
